var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { forwardRef } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import SearchIcon from '@mui/icons-material/Search';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Dialog, DialogContent, DialogTitle } from 'root/src/client/v2/common/components/ui/dialog';
// shadcn Command ported onto our conventions: cmdk under the hood, redesign tokens, MUI icons (not
// lucide), and CommandDialog reuses our Dialog/DialogContent (so it inherits the `redesign` scope,
// z-[1101] above the navbar, and the dialog open/close animations).
export const Command = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<CommandPrimitive ref={ref} className={cn('flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground', className)} {...props}/>);
});
Command.displayName = CommandPrimitive.displayName;
export const CommandDialog = (_a) => {
    var { children, title = 'Command menu' } = _a, props = __rest(_a, ["children", "title"]);
    return (<Dialog {...props}>
		{/* Hide the default Dialog close (X) - the palette closes on Esc / outside click; keep it edge to edge. */}
		<DialogContent className="max-w-[640px] gap-0 overflow-hidden p-0 [&>button]:hidden">
			<DialogTitle className="sr-only">{title}</DialogTitle>
			<Command>{children}</Command>
		</DialogContent>
	</Dialog>);
};
export const CommandInput = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<div className="flex items-center border-b border-solid border-border px-3.5">
		<SearchIcon sx={{ fontSize: 18 }} className="mr-2.5 shrink-0 text-muted-foreground"/>
		<CommandPrimitive.Input ref={ref} className={cn('flex h-12 w-full bg-transparent py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50', className)} {...props}/>
	</div>);
});
CommandInput.displayName = CommandPrimitive.Input.displayName;
export const CommandList = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<CommandPrimitive.List ref={ref} className={cn('max-h-[360px] overflow-y-auto overflow-x-hidden scroll-py-1 p-1.5', className)} {...props}/>);
});
CommandList.displayName = CommandPrimitive.List.displayName;
export const CommandEmpty = forwardRef((props, ref) => (<CommandPrimitive.Empty ref={ref} className="py-8 text-center text-sm text-muted-foreground" {...props}/>));
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;
export const CommandGroup = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<CommandPrimitive.Group ref={ref} className={cn('overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground', className)} {...props}/>);
});
CommandGroup.displayName = CommandPrimitive.Group.displayName;
export const CommandSeparator = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<CommandPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props}/>);
});
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;
export const CommandItem = forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<CommandPrimitive.Item ref={ref} className={cn('relative flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-2.5 text-sm text-muted-foreground outline-none transition-colors data-[selected=true]:bg-white/[0.06] data-[selected=true]:text-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50', className)} {...props}/>);
});
CommandItem.displayName = CommandPrimitive.Item.displayName;
export const CommandShortcut = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (<span className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)} {...props}/>);
};
CommandShortcut.displayName = 'CommandShortcut';
